import React, { FC, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { Organization } from 'models/organization';

type InvoiceLabSettingsProps = {
    currentLab: Organization;
    onInvoiceNumberTypeChange: (value: string | undefined) => void;
    onStartNumberChange: (value: number | undefined) => void;
};

const InvoiceLabSettings: FC<InvoiceLabSettingsProps> = ({
    currentLab,
    onInvoiceNumberTypeChange,
    onStartNumberChange,
}: InvoiceLabSettingsProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'common', 'organization']);
    const currentOrganization = currentOrgVar();
    const [invoiceNumberType, setInvoiceNumberType] = React.useState<string | undefined>('');
    const [currentStartingNumber, setCurrentStartingNumber] = React.useState<number | undefined>();
    const isFortnoxEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.FORTNOX,
    );
    const isVismaEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.VISMA,
    );
    const isExternalInvoiceEnbled = isFortnoxEnabled || isVismaEnabled;
    const isIncrementalSelected = invoiceNumberType === 'INCREMENTAL';

    useEffect(() => {
        setInvoiceNumberType(currentLab.invoicing.configuration?.invoiceNumberType);
        setCurrentStartingNumber(currentLab.invoicing.configuration?.currentStartingNumber);
    }, [currentLab]);

    const handleStartNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(event.target.value);
        setCurrentStartingNumber(value);

        if (!isNaN(value)) {
            setCurrentStartingNumber(value);
            onStartNumberChange(value);
        } else {
            setCurrentStartingNumber(0);
        }
    };

    const handleInvoiceNumberType = (event: React.SyntheticEvent<Element, Event>): void => {
        const value = (event.target as HTMLInputElement).value;
        setInvoiceNumberType(value);
        onInvoiceNumberTypeChange(value);
    };

    return (
        <Fragment>
            <Box mb={2}>
                <PageTitle headingStyle="h3" title={translate('lab-settings:INVOICE_NUMBERS')} />
                <FormControl>
                    <RadioGroup>
                        <FormControlLabel
                            value="MANUAL"
                            control={<Radio />}
                            label={translate('lab-settings:MANUAL_INVOICE_NUMBER')}
                            checked={invoiceNumberType === 'MANUAL'}
                            onChange={handleInvoiceNumberType}
                            disabled={isExternalInvoiceEnbled || currentLab.invoicing.configuration?.isShared}
                        />

                        <FormControlLabel
                            value="INCREMENTAL"
                            control={<Radio />}
                            checked={isIncrementalSelected}
                            onChange={handleInvoiceNumberType}
                            disabled={isExternalInvoiceEnbled || currentLab.invoicing.configuration?.isShared}
                            label={
                                <Fragment>
                                    {translate('lab-settings:AUTO_INCREMENTING_INVOICE_NUMBER')}
                                    {isIncrementalSelected && (
                                        <TextField
                                            variant="standard"
                                            hiddenLabel
                                            size="small"
                                            type="int"
                                            disabled={isFortnoxEnabled || currentLab.invoicing.configuration?.isShared}
                                            sx={{ maxWidth: '100px', margin: '0 10px' }}
                                            inputProps={{ style: { textAlign: 'left' } }}
                                            value={currentStartingNumber}
                                            onChange={handleStartNumber}
                                        />
                                    )}
                                </Fragment>
                            }
                        />

                        {isExternalInvoiceEnbled ? (
                            <FormControlLabel
                                value="FORTNOX"
                                control={<Radio />}
                                label={translate('lab-settings:EXTERNAL_INVOICE_NUMBER')}
                                checked={invoiceNumberType === 'FORTNOX'}
                                onChange={handleInvoiceNumberType}
                                disabled={currentLab.invoicing.configuration?.isShared}
                            />
                        ) : undefined}
                    </RadioGroup>
                </FormControl>
            </Box>
        </Fragment>
    );
};

export default InvoiceLabSettings;
